<template>
    <div class="min-h-screen page-bg relative overflow-hidden">

        <!-- Performer side backgrounds -->
        <template v-if="castPerformer">
            <div class="hidden xl:block fixed top-0 left-0 w-56 h-full pointer-events-none z-0">
                <img :src="castPerformer.thumbnail_url" :alt="castPerformer.name" class="w-full h-full object-cover object-top opacity-30" />
                <div class="absolute inset-0 bg-gradient-to-r from-transparent to-[#0f0f17]" />
                <div class="absolute inset-0 bg-gradient-to-b from-[#0f0f17] via-transparent to-[#0f0f17]" />
            </div>
            <div class="hidden xl:block fixed top-0 right-0 w-56 h-full pointer-events-none z-0" style="transform: scaleX(-1)">
                <img :src="castPerformer.thumbnail_url" :alt="castPerformer.name" class="w-full h-full object-cover object-top opacity-30" />
                <div class="absolute inset-0 bg-gradient-to-r from-transparent to-[#0f0f17]" />
                <div class="absolute inset-0 bg-gradient-to-b from-[#0f0f17] via-transparent to-[#0f0f17]" />
            </div>
        </template>

        <HeaderV2
            :isUserLoggedIn="isUserLoggedIn"
            :isGoldUser="isGoldUser"
            :routePrefix="routePrefix"
            :requiresAgeVerification="requiresAgeVerification"
            @goldJoinClick="isBuyStarsModalOpen = true"
        />

        <main class="max-w-5xl mx-auto px-4 py-8 sm:py-12">

            <!-- ── Success State ── -->
            <div v-if="submitted" class="rounded-2xl page-card px-6 pt-8 pb-8 flex flex-col items-center text-center gap-5">
                <div class="relative">
                    <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/30 to-purple-600/20 border border-violet-500/30 flex items-center justify-center">
                        <Clapperboard class="w-8 h-8 text-violet-400" />
                    </div>
                    <div class="absolute -inset-1 rounded-2xl bg-violet-500/10 blur-md -z-10" />
                </div>
                <div>
                    <h2 class="text-xl font-bold text-white mb-1.5">Pitch Submitted!</h2>
                    <p class="text-white/50 text-sm leading-relaxed max-w-xs mx-auto">
                        Your pitch is under review. Our team will reach out to the requested performers and assess feasibility.
                    </p>
                </div>
                <div class="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-4 text-left space-y-3">
                    <div v-for="(item, i) in successSteps" :key="i" class="flex items-start gap-3">
                        <span class="w-5 h-5 rounded-full bg-violet-600/20 border border-violet-500/30 text-violet-400 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">{{ i + 1 }}</span>
                        <p class="text-white/55 text-xs leading-relaxed">{{ item }}</p>
                    </div>
                </div>
                <button
                    v-if="submittedSlug"
                    @click="router.visit(`/studio/castings/${submittedSlug}`)"
                    class="w-full py-3 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold rounded-xl text-sm transition-all active:scale-[0.98] shadow-lg shadow-violet-600/30"
                >
                    View My Pitch
                </button>
                <button
                    v-else
                    @click="goToCastingsPage"
                    class="w-full py-3 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold rounded-xl text-sm transition-all active:scale-[0.98] shadow-lg shadow-violet-600/30"
                >
                    View My Pitches
                </button>
            </div>

            <!-- ── Wizard ── -->
            <template v-else>

                <!-- Page Header -->
                <div class="mb-6">
                    <h1 class="text-3xl font-extrabold leading-tight">
                        <template v-if="castPerformer">
                            <span class="text-white">Submit Your Production Pitch for </span><a :href="castPerformerUrl" target="_blank" class="text-violet-400 hover:text-violet-300 transition-colors">{{ castPerformer.name }}</a>
                        </template>
                        <template v-else>
                            <span class="text-white">Submit Your </span><span class="text-violet-400">Production Pitch</span>
                        </template>
                    </h1>
                    <div class="mt-3 mb-3 h-px bg-white/10" />
                    <p class="text-white/45 text-sm leading-relaxed">
                        <template v-if="castPerformer">Tell us your vision for a scene featuring <a :href="castPerformerUrl" target="_blank" class="text-violet-400 hover:text-violet-300 font-medium transition-colors">{{ castPerformer.name }}</a> and help us decide if it gets made.</template>
                        <template v-else>Help shape the future of our content. Share your creative ideas and help us decide what premium productions get made next.</template>
                    </p>
                </div>

                <!-- Step Indicator -->
                <div class="mb-8">
                    <div class="flex items-center gap-2">
                        <template v-for="(step, i) in steps" :key="step.key">
                            <button
                                type="button"
                                @click="i + 1 < currentStep ? currentStep = i + 1 : null"
                                :class="[
                                    'flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300 flex-1 justify-center',
                                    i + 1 === currentStep
                                        ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/30'
                                        : i + 1 < currentStep
                                            ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 cursor-pointer hover:bg-emerald-500/25'
                                            : 'bg-white/5 border border-white/8 text-white/25 cursor-default'
                                ]"
                            >
                                <span :class="[
                                    'w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0',
                                    i + 1 === currentStep ? 'bg-white/20' : i + 1 < currentStep ? 'bg-emerald-500/30' : 'bg-white/10'
                                ]">
                                    <Check v-if="i + 1 < currentStep" class="w-2.5 h-2.5" />
                                    <span v-else>{{ i + 1 }}</span>
                                </span>
                                {{ step.label }}
                            </button>
                        </template>
                    </div>
                </div>

                <!-- Content row: card + performer panel -->
                <div class="flex gap-8 items-start mb-4">

                <!-- Step Body -->
                <div class="flex-1 min-w-0 rounded-2xl page-card px-5 sm:px-6 py-6">
                    <Transition :name="slideDirection" mode="out-in">
                        <div :key="currentStep">

                            <!-- ── Step 1: The Pitch ── -->
                            <template v-if="currentStep === 1">
                                <div class="flex items-center gap-3 mb-5">
                                    <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-600/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                                        <Lightbulb class="w-4.5 h-4.5 text-amber-400" />
                                    </div>
                                    <div>
                                        <h2 class="text-base font-bold text-white leading-tight">The Pitch</h2>
                                        <p class="text-white/35 text-[11px] leading-snug">Name your idea and sell the concept.</p>
                                    </div>
                                </div>

                                <div class="mb-5">
                                    <label class="field-label">Pitch Title</label>
                                    <p class="text-white/35 text-[11px] mb-1.5 leading-snug">Give your pitch a name that hooks people.</p>
                                    <input
                                        v-model="form.title"
                                        type="text"
                                        placeholder="e.g. The Tutor Next Door"
                                        maxlength="120"
                                        :class="['field-input', (fieldErrors.title || stepErrors.title) ? 'field-error' : '']"
                                    />
                                    <p v-if="fieldErrors.title || stepErrors.title" class="field-error-msg">{{ fieldErrors.title || stepErrors.title }}</p>
                                </div>

                                <div class="mb-5">
                                    <label class="field-label">The Setup</label>
                                    <p class="text-white/35 text-[11px] mb-1.5 leading-snug">One or two sentences that sell your concept — this is what people see in the feed.</p>
                                    <textarea
                                        v-model="form.description"
                                        placeholder="e.g. A college student hires a private tutor, but the lessons quickly go off-syllabus when she realizes he's the guy she's been flirting with at the coffee shop..."
                                        rows="4"
                                        maxlength="280"
                                        :class="['field-input resize-none', (fieldErrors.description || stepErrors.description) ? 'field-error' : '']"
                                    />
                                    <div class="flex justify-between items-start mt-1">
                                        <p v-if="fieldErrors.description || stepErrors.description" class="field-error-msg">{{ fieldErrors.description || stepErrors.description }}</p>
                                        <span :class="['text-[11px] text-white/25', (fieldErrors.description || stepErrors.description) ? 'ml-auto' : 'w-full text-right']">{{ form.description.length }}/280</span>
                                    </div>
                                </div>

                                <div class="mb-2">
                                    <label class="field-label">Director's Notes <span class="text-white/25 font-normal">(optional)</span></label>
                                    <p class="text-white/35 text-[11px] mb-1.5 leading-snug">Describe the arc, pacing, key moments, or anything that brings your vision to life.</p>
                                    <textarea
                                        v-model="form.directorNotes"
                                        placeholder="e.g. Starts slow with tension building — eye contact, accidental touches. She makes the first move…"
                                        rows="4"
                                        maxlength="1000"
                                        :class="['field-input resize-none', fieldErrors.director_notes ? 'field-error' : '']"
                                    />
                                    <p v-if="fieldErrors.director_notes" class="field-error-msg">{{ fieldErrors.director_notes }}</p>
                                </div>
                            </template>

                            <!-- ── Step 2: The Cast ── -->
                            <template v-else-if="currentStep === 2">
                                <div class="flex items-center gap-3 mb-5">
                                    <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-600/10 border border-violet-500/20 flex items-center justify-center shrink-0">
                                        <Users class="w-4.5 h-4.5 text-violet-400" />
                                    </div>
                                    <div>
                                        <h2 class="text-base font-bold text-white leading-tight">Inspiration Cast</h2>
                                        <p class="text-white/35 text-[11px] leading-snug">Pick performers or name specific performers that inspire your pitch.</p>
                                    </div>
                                </div>

                                <div class="mb-5">
                                    <div class="flex items-center justify-between mb-1">
                                        <label class="field-label !mb-0">Performers</label>
                                        <button
                                            v-if="form.performers.length || form.performerDescription"
                                            type="button"
                                            @click="clearAllPerformers"
                                            class="inline-flex items-center gap-1 text-[11px] font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/15 border border-red-500/20 hover:border-red-500/35 px-2 py-0.5 rounded-full transition-all"
                                        >
                                            <X class="w-2.5 h-2.5" />
                                            Clear all
                                        </button>
                                    </div>
                                    <p class="text-white/40 text-xs mb-1.5">Select from our list of performers or name specific performers</p>
                                    <div ref="performerInputContainer" class="relative">
                                        <div :class="[
                                            'bg-zinc-900/80 border rounded-xl px-3 py-2 transition-all',
                                            fieldErrors.performers
                                                ? 'border-red-500/60 focus-within:border-red-500'
                                                : 'border-white/10 focus-within:border-violet-500/60 focus-within:shadow-[0_0_0_3px_rgba(139,92,246,0.08)]'
                                        ]">
                                            <div v-if="form.performers.length || form.performerDescription" class="flex flex-wrap gap-1.5 mb-2">
                                                <span
                                                    v-for="performer in form.performers"
                                                    :key="performer.id"
                                                    :class="['inline-flex items-center gap-1.5 border text-xs font-medium pl-0.5 py-0.5 rounded-full', isLockedPerformer(performer.id) ? 'bg-violet-600/25 border-violet-400/40 text-violet-200 pr-1.5' : 'bg-violet-600/15 border-violet-500/25 text-violet-300 pr-2']"
                                                >
                                                    <img :src="performer.thumbnail_url" :alt="performer.name" class="w-5 h-5 rounded-full object-cover object-top shrink-0" />
                                                    {{ performer.name }}
                                                    <Lock v-if="isLockedPerformer(performer.id)" class="w-3 h-3 text-violet-400/60 shrink-0" />
                                                    <button v-else type="button" @click.stop="removePerformer(performer.id)" class="text-violet-400/50 hover:text-violet-300 transition-colors">
                                                        <X class="w-3 h-3" />
                                                    </button>
                                                </span>
                                                <span
                                                    v-if="form.performerDescription"
                                                    class="inline-flex items-center gap-1.5 bg-amber-500/15 border border-amber-500/25 text-amber-300 text-xs font-medium pl-2 pr-2 py-0.5 rounded-full"
                                                >
                                                    <Pencil class="w-3 h-3 shrink-0 opacity-60" />
                                                    {{ form.performerDescription }}
                                                    <button type="button" @click.stop="form.performerDescription = ''" class="text-amber-400/50 hover:text-amber-300 transition-colors">
                                                        <X class="w-3 h-3" />
                                                    </button>
                                                </span>
                                            </div>
                                            <div v-if="form.performers.length < 4" class="flex items-center gap-2">
                                                <input
                                                    v-model="performerInput"
                                                    type="text"
                                                    placeholder="Search performers…"
                                                    autocomplete="off"
                                                    class="flex-1 bg-transparent border-0 text-white text-sm placeholder:text-white/25 outline-none ring-0 focus:outline-none focus:ring-0"
                                                    @focus="performerInputFocused = true"
                                                    @blur="performerInputFocused = false; highlightedPerformerIndex = -1"
                                                    @keydown="onPerformerKeydown"
                                                />
                                                <svg v-if="isLoadingPerformers" class="animate-spin w-3.5 h-3.5 text-white/30 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                                </svg>
                                            </div>
                                            <p v-else class="text-white/30 text-xs py-1">Maximum of 4 performers reached</p>
                                        </div>
                                        <!-- Suggestions dropdown -->
                                        <Teleport to="body">
                                            <div
                                                v-if="performerInputFocused && (suggestions.length || canAddAsDescription) && performerDropdownStyle"
                                                ref="performerDropdown"
                                                :style="performerDropdownStyle"
                                                class="fixed z-[9999] bg-zinc-900 border border-white/10 rounded-xl overflow-y-auto casting-scroll shadow-2xl shadow-black/60"
                                            >
                                                <button
                                                    v-for="(performer, index) in suggestions"
                                                    :key="performer.id"
                                                    type="button"
                                                    @mousedown.prevent
                                                    @click="selectPerformer(performer)"
                                                    :class="['w-full text-left px-3 py-2 text-sm text-white transition-colors flex items-center gap-2.5', index === highlightedPerformerIndex ? 'bg-zinc-700' : 'hover:bg-zinc-800']"
                                                >
                                                    <div class="w-9 h-9 rounded-full overflow-hidden ring-1 ring-white/10 shrink-0 bg-zinc-800">
                                                        <img :src="performer.thumbnail_url" :alt="performer.name" class="w-full h-full object-cover object-top scale-125 origin-top" />
                                                    </div>
                                                    <span>{{ performer.name }}</span>
                                                </button>
                                                <button
                                                    v-if="canAddAsDescription"
                                                    type="button"
                                                    @mousedown.prevent
                                                    @click="addAsDescription"
                                                    :class="['w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2.5', suggestions.length ? 'border-t border-white/5' : '', highlightedPerformerIndex === suggestions.length ? 'bg-zinc-700' : 'hover:bg-zinc-800']"
                                                >
                                                    <div class="w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/20 shrink-0 flex items-center justify-center">
                                                        <Pencil class="w-3.5 h-3.5 text-amber-400" />
                                                    </div>
                                                    <span class="text-white/50">Use as description: </span>
                                                    <span class="text-amber-300 font-medium truncate">"{{ performerInput.trim() }}"</span>
                                                </button>
                                            </div>
                                        </Teleport>
                                        <p v-if="fieldErrors.performers" class="field-error-msg">{{ fieldErrors.performers }}</p>
                                    </div>

                                    <!-- Performer carousel -->
                                    <Transition name="carousel-reveal" @after-enter="startCarouselRaf">
                                        <div
                                            v-if="form.performers.length"
                                            class="mt-3 rounded-2xl bg-white/[0.03] border border-white/[0.07] p-3"
                                            @mouseenter="pauseCarousel"
                                            @mouseleave="resumeCarousel"
                                        >
                                            <!-- scrollable viewport -->
                                            <div
                                                ref="carouselViewport"
                                                class="relative overflow-x-scroll carousel-viewport rounded-xl"
                                                @wheel.prevent="onWheel"
                                                @touchstart.passive="onManualScroll"
                                                @touchmove.passive="onManualScroll"
                                            >
                                                <!-- edge fades -->
                                                <template v-if="shouldAnimate">
                                                    <div class="sticky left-0 w-8 h-full absolute inset-y-0 bg-gradient-to-r from-zinc-950/80 to-transparent z-10 pointer-events-none" />
                                                    <div class="sticky right-0 w-8 h-full absolute inset-y-0 right-0 bg-gradient-to-l from-zinc-950/80 to-transparent z-10 pointer-events-none" />
                                                </template>

                                                <!-- track -->
                                                <div class="flex" :style="{ gap: `${CARD_GAP}px` }">
                                                    <div
                                                        v-for="(performer, i) in carouselItems"
                                                        :key="`${performer.id}-${i}`"
                                                        class="relative shrink-0 group"
                                                        :style="{ width: `${cardWidth}px` }"
                                                    >
                                                        <div class="relative rounded-xl overflow-hidden bg-zinc-900 ring-1 ring-white/10 aspect-[2/3] transition-all duration-300 group-hover:ring-violet-500/50 group-hover:shadow-lg group-hover:shadow-violet-900/30">
                                                            <img :src="performer.thumbnail_url" :alt="performer.name" class="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                                                            <div class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                                                            <p class="absolute bottom-2 inset-x-1.5 text-white text-[10px] font-semibold text-center leading-snug drop-shadow-md">{{ performer.name }}</p>
                                                            <div
                                                                v-if="isLockedPerformer(performer.id)"
                                                                class="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-violet-600/80 border border-violet-400/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                                                :aria-label="`${performer.name} is locked`"
                                                            >
                                                                <Lock class="w-2.5 h-2.5 text-white" />
                                                            </div>
                                                            <button
                                                                v-else
                                                                type="button"
                                                                @click="removePerformer(performer.id)"
                                                                class="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-black/70 hover:bg-red-500/90 border border-white/15 hover:border-red-400/60 flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100"
                                                                :aria-label="`Remove ${performer.name}`"
                                                            >
                                                                <X class="w-2.5 h-2.5 text-white" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Transition>
                                </div>

                                <p v-if="fieldErrors.performer_description" class="field-error-msg mt-1">{{ fieldErrors.performer_description }}</p>
                                <p v-if="stepErrors.cast" class="field-error-msg mt-1">{{ stepErrors.cast }}</p>
                            </template>

                            <!-- ── Step 3: Scene Details ── -->
                            <template v-else-if="currentStep === 3">
                                <div class="flex items-center gap-3 mb-5">
                                    <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-600/10 border border-teal-500/20 flex items-center justify-center shrink-0">
                                        <Film class="w-4.5 h-4.5 text-teal-400" />
                                    </div>
                                    <div>
                                        <h2 class="text-base font-bold text-white leading-tight">Scene Details</h2>
                                        <p class="text-white/35 text-[11px] leading-snug">Set the format, location, and categories.</p>
                                    </div>
                                </div>

                                <!-- Format -->
                                <div class="mb-5">
                                    <label class="field-label">Format <span class="text-white/25 font-normal">(required)</span></label>
                                    <div class="grid grid-cols-4 gap-2 mt-1.5">
                                        <button
                                            v-for="option in formatOptions"
                                            :key="option.label"
                                            type="button"
                                            @click="form.format = form.format === option.label ? null : option.label"
                                            :class="[
                                                'flex flex-col items-center gap-2.5 pt-3 pb-2.5 px-2 rounded-xl border transition-all duration-200 active:scale-95',
                                                form.format === option.label
                                                    ? 'bg-teal-600/10 border-teal-500/40 shadow-[0_0_14px_rgba(20,184,166,0.15)]'
                                                    : 'bg-white/[0.02] border-white/10 hover:border-white/20 hover:bg-white/[0.04]'
                                            ]"
                                        >
                                            <!-- Aspect ratio shape with icon inside -->
                                            <div class="w-full h-14 flex items-center justify-center overflow-hidden">
                                                <!-- Mixed Reality: two offset shapes with icon on top -->
                                                <div v-if="option.layered" class="relative flex items-center justify-center" :class="[option.width, option.aspect]">
                                                    <div :class="['absolute inset-0 rounded border translate-x-1.5 -translate-y-1 opacity-50 transition-colors', form.format === option.label ? 'bg-teal-500/10 border-teal-500/20' : 'bg-white/5 border-white/8']" />
                                                    <div :class="['absolute inset-0 rounded border transition-colors', form.format === option.label ? 'bg-teal-500/15 border-teal-500/30' : 'bg-white/8 border-white/10']" />
                                                    <component :is="option.icon" :class="['relative z-10 w-3.5 h-3.5 transition-colors', form.format === option.label ? 'text-teal-300' : 'text-white/25']" />
                                                </div>
                                                <!-- All other formats -->
                                                <div v-else class="relative flex items-center justify-center shrink-0 rounded border transition-colors" :class="[option.width, option.aspect, form.format === option.label ? 'bg-teal-500/15 border-teal-500/30' : 'bg-white/8 border-white/10']">
                                                    <component :is="option.icon" :class="['w-3.5 h-3.5 transition-colors', form.format === option.label ? 'text-teal-300' : 'text-white/25']" />
                                                </div>
                                            </div>
                                            <!-- Label -->
                                            <div class="text-center">
                                                <p :class="['text-[11px] font-semibold leading-tight transition-colors', form.format === option.label ? 'text-teal-300' : 'text-white/55']">{{ option.label }}</p>
                                                <p :class="['text-[9px] font-medium mt-0.5 transition-colors', form.format === option.label ? 'text-teal-400/60' : 'text-white/20']">{{ option.subtitle }}</p>
                                            </div>
                                        </button>
                                    </div>
                                    <p v-if="stepErrors.format || fieldErrors.format" class="field-error-msg">{{ stepErrors.format || fieldErrors.format }}</p>
                                </div>

                                <!-- Setting -->
                                <div class="mb-5">
                                    <label class="field-label">Setting <span class="text-white/25 font-normal">(optional)</span></label>
                                    <p class="text-white/35 text-[11px] mb-2 leading-snug">Where does the scene take place?</p>
                                    <div class="flex flex-wrap gap-1.5 mb-2">
                                        <button
                                            v-for="option in settingOptions"
                                            :key="option.label"
                                            type="button"
                                            @click="toggleSetting(option.label)"
                                            :class="[
                                                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 active:scale-95',
                                                form.setting.includes(option.label)
                                                    ? 'bg-teal-600/20 text-teal-300 border-teal-500/35'
                                                    : 'bg-white/[0.03] text-white/50 border-white/10 hover:border-white/15 hover:text-white/80'
                                            ]"
                                        >
                                            <component :is="option.icon" :class="['w-3 h-3 shrink-0', form.setting.includes(option.label) ? 'opacity-100' : option.color]" />
                                            {{ option.label }}
                                        </button>
                                        <button
                                            type="button"
                                            @click="showCustomSetting = !showCustomSetting"
                                            :class="[
                                                'px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 active:scale-95',
                                                showCustomSetting
                                                    ? 'bg-teal-600/20 text-teal-300 border-teal-500/35'
                                                    : 'bg-white/[0.03] text-white/50 border-white/10 hover:border-white/15 hover:text-white/100'
                                            ]"
                                        >
                                            + Custom
                                        </button>
                                    </div>
                                    <input
                                        v-if="showCustomSetting"
                                        v-model="settingCustom"
                                        type="text"
                                        placeholder='Describe your setting — e.g. "Rooftop bar at sunset"'
                                        maxlength="120"
                                        :class="['field-input', fieldErrors.setting ? 'field-error' : '']"
                                    />
                                    <p v-if="fieldErrors.setting" class="field-error-msg">{{ fieldErrors.setting }}</p>
                                </div>

                                <!-- Tags -->
                                <div class="mb-2">
                                    <label class="field-label">Category / Tags <span class="text-white/25 font-normal">(optional)</span></label>
                                    <p class="text-white/35 text-[11px] mb-1.5 leading-snug">Help people find your pitch — select all that apply.</p>
                                    <div ref="tagInputContainer" class="relative">
                                        <div :class="[
                                            'bg-zinc-900/80 border rounded-xl px-3 py-2 transition-all',
                                            tagInput.trim().length >= 2 && !tagSuggestions.length && !isLoadingTags
                                                ? 'border-red-500/60'
                                                : 'border-white/10 focus-within:border-violet-500/60 focus-within:shadow-[0_0_0_3px_rgba(139,92,246,0.08)]'
                                        ]">
                                            <div v-if="form.tags.length" class="flex flex-wrap gap-1.5 mb-2">
                                                <span
                                                    v-for="tag in form.tags"
                                                    :key="tag.id"
                                                    class="inline-flex items-center gap-1 bg-white/5 border border-white/10 text-white/60 text-xs font-medium px-2 py-0.5 rounded-full"
                                                >
                                                    #{{ tag.name }}
                                                    <button type="button" @click="removeTag(tag.id)" class="text-white/25 hover:text-white/50 transition-colors">
                                                        <X class="w-3 h-3" />
                                                    </button>
                                                </span>
                                            </div>
                                            <div class="flex items-center gap-2">
                                                <input
                                                    v-model="tagInput"
                                                    type="text"
                                                    placeholder="Search tags…"
                                                    autocomplete="off"
                                                    class="flex-1 bg-transparent border-0 text-white text-sm placeholder:text-white/25 outline-none ring-0 focus:outline-none focus:ring-0"
                                                    @keydown.escape="tagInput = ''"
                                                />
                                                <svg v-if="isLoadingTags" class="animate-spin w-3.5 h-3.5 text-white/30 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                                </svg>
                                            </div>
                                        </div>
                                        <Teleport to="body">
                                            <div
                                                v-if="tagSuggestions.length && tagDropdownStyle"
                                                :style="tagDropdownStyle"
                                                class="fixed z-[9999] bg-zinc-900 border border-white/10 rounded-xl overflow-y-auto casting-scroll shadow-2xl shadow-black/60"
                                            >
                                                <button
                                                    v-for="tag in tagSuggestions"
                                                    :key="tag.id"
                                                    type="button"
                                                    @mousedown.prevent
                                                    @click="selectTag(tag)"
                                                    class="w-full text-left px-3 py-2 text-sm text-white/70 hover:bg-zinc-800 hover:text-white transition-colors"
                                                >
                                                    #{{ tag.name }}
                                                </button>
                                            </div>
                                        </Teleport>
                                        <p v-if="tagInput.trim().length >= 2 && !tagSuggestions.length && !isLoadingTags" class="text-white/30 text-[11px] mt-1.5">No tags found</p>
                                    </div>
                                    <p v-if="fieldErrors.tags" class="field-error-msg mt-1">{{ fieldErrors.tags }}</p>
                                </div>
                            </template>

                            <!-- ── Step 4: Finish ── -->
                            <template v-else-if="currentStep === 4">
                                <div class="flex items-center gap-3 mb-5">
                                    <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-600/10 border border-violet-500/20 flex items-center justify-center shrink-0">
                                        <Rocket class="w-4.5 h-4.5 text-violet-400" />
                                    </div>
                                    <div>
                                        <h2 class="text-base font-bold text-white leading-tight">Review your Pitch</h2>
                                        <p class="text-white/35 text-[11px] leading-snug">Double-check everything before submitting.</p>
                                    </div>
                                </div>

                                <!-- Summary -->
                                <div class="mb-5 rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5 overflow-hidden">

                                    <!-- Title -->
                                    <div class="px-4 py-3 flex items-start justify-between gap-2">
                                        <div class="flex-1 min-w-0">
                                            <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Title</p>
                                            <p v-if="form.title" class="text-white text-sm font-semibold">{{ form.title }}</p>
                                            <p v-else class="text-white/20 text-xs italic">No title entered</p>
                                        </div>
                                        <button type="button" @click="goToStep(1)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                            <Pencil class="w-3 h-3 text-white/40" />
                                        </button>
                                    </div>

                                    <!-- Description -->
                                    <div class="px-4 py-3 flex items-start justify-between gap-2">
                                        <div class="flex-1 min-w-0">
                                            <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-0.5">The Setup</p>
                                            <p v-if="form.description" class="text-white/70 text-xs leading-relaxed">{{ form.description }}</p>
                                            <p v-else class="text-white/20 text-xs italic">No description entered</p>
                                        </div>
                                        <button type="button" @click="goToStep(1)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                            <Pencil class="w-3 h-3 text-white/40" />
                                        </button>
                                    </div>

                                    <!-- Director's Notes -->
                                    <div class="px-4 py-3 flex items-start justify-between gap-2">
                                        <div class="flex-1 min-w-0">
                                            <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Director's Notes</p>
                                            <p v-if="form.directorNotes" class="text-white/70 text-xs leading-relaxed">{{ form.directorNotes }}</p>
                                            <p v-else class="text-white/20 text-xs italic">None</p>
                                        </div>
                                        <button type="button" @click="goToStep(3)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                            <Pencil class="w-3 h-3 text-white/40" />
                                        </button>
                                    </div>

                                    <!-- Cast -->
                                    <div class="px-4 py-3 flex items-start justify-between gap-2">
                                        <div class="flex-1 min-w-0">
                                            <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Cast</p>
                                            <div v-if="form.performers.length" class="flex flex-wrap gap-1.5">
                                                <span
                                                    v-for="p in visiblePerformerChips"
                                                    :key="p.id"
                                                    class="inline-flex items-center gap-1.5 bg-violet-600/15 border border-violet-500/25 text-violet-300 text-xs font-medium pl-0.5 pr-2 py-0.5 rounded-full"
                                                >
                                                    <img :src="p.thumbnail_url" :alt="p.name" class="w-5 h-5 rounded-full object-cover object-top shrink-0" />
                                                    {{ p.name }}
                                                </span>
                                                <span v-if="hiddenPerformerChipCount > 0" class="inline-flex items-center bg-violet-600/10 border border-violet-500/20 text-violet-400 text-xs font-medium px-2 py-0.5 rounded-full">
                                                    +{{ hiddenPerformerChipCount }} more
                                                </span>
                                            </div>
                                            <p v-else-if="form.performerDescription" class="text-white/60 text-xs">{{ form.performerDescription }}</p>
                                            <p v-else class="text-white/20 text-xs italic">No performers selected</p>
                                        </div>
                                        <button type="button" @click="goToStep(2)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                            <Pencil class="w-3 h-3 text-white/40" />
                                        </button>
                                    </div>

                                    <!-- Format -->
                                    <div class="px-4 py-3 flex items-start justify-between gap-2">
                                        <div class="flex-1 min-w-0">
                                            <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-2">Format</p>
                                            <div v-if="selectedFormatOption" class="inline-flex items-center gap-2 bg-teal-600/10 border border-teal-500/30 rounded-xl px-3 py-2">
                                                <p class="text-teal-300 text-xs font-semibold leading-tight">{{ selectedFormatOption.label }}</p>
                                                <span class="text-teal-500/50 text-[10px]">·</span>
                                                <p class="text-teal-400/60 text-[10px] font-medium">{{ selectedFormatOption.subtitle }}</p>
                                            </div>
                                            <p v-else class="text-white/20 text-xs italic">No format selected</p>
                                        </div>
                                        <button type="button" @click="goToStep(3)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                            <Pencil class="w-3 h-3 text-white/40" />
                                        </button>
                                    </div>

                                    <!-- Setting -->
                                    <div class="px-4 py-3 flex items-start justify-between gap-2">
                                        <div class="flex-1 min-w-0">
                                            <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-1">Setting</p>
                                            <div v-if="form.setting.length || (showCustomSetting && settingCustom)" class="flex flex-wrap gap-1">
                                                <span v-for="s in form.setting" :key="s" class="text-[10px] bg-teal-600/15 border border-teal-500/25 text-teal-300 px-1.5 py-0.5 rounded-full">{{ s }}</span>
                                                <span v-if="showCustomSetting && settingCustom" class="text-[10px] bg-teal-600/15 border border-teal-500/25 text-teal-300 px-1.5 py-0.5 rounded-full">{{ settingCustom }}</span>
                                            </div>
                                            <p v-else class="text-white/20 text-xs italic">No setting selected</p>
                                        </div>
                                        <button type="button" @click="goToStep(3)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                            <Pencil class="w-3 h-3 text-white/40" />
                                        </button>
                                    </div>

                                    <!-- Tags -->
                                    <div class="px-4 py-3 flex items-start justify-between gap-2">
                                        <div class="flex-1 min-w-0">
                                            <p class="text-white/35 text-[10px] font-semibold uppercase tracking-wider mb-1.5">Tags</p>
                                            <div v-if="form.tags.length" class="flex flex-wrap gap-1">
                                                <span v-for="tag in form.tags" :key="tag.id" class="text-[10px] bg-white/5 border border-white/10 text-white/55 px-1.5 py-0.5 rounded-full">#{{ tag.name }}</span>
                                            </div>
                                            <p v-else class="text-white/20 text-xs italic">No tags selected</p>
                                        </div>
                                        <button type="button" @click="goToStep(3)" class="shrink-0 w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors mt-0.5">
                                            <Pencil class="w-3 h-3 text-white/40" />
                                        </button>
                                    </div>

                                </div>

                                <!-- Terms agreement -->
                                <label class="mb-5 flex items-start gap-2.5 cursor-pointer w-fit group">
                                    <span
                                        :class="[
                                            'mt-0.5 shrink-0 w-4 h-4 rounded border transition-all flex items-center justify-center',
                                            agreedToTerms
                                                ? 'bg-violet-600 border-violet-500 shadow-[0_0_8px_rgba(139,92,246,0.4)]'
                                                : 'bg-transparent border-white/15 group-hover:border-white/30'
                                        ]"
                                        @click.prevent="agreedToTerms = !agreedToTerms"
                                    >
                                        <svg v-if="agreedToTerms" class="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
                                            <path d="M1 4l3 3 5-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </span>
                                    <p class="text-white/40 text-[11px] leading-snug select-none" @click.prevent="agreedToTerms = !agreedToTerms">
                                        By submitting, I agree to the
                                        <a @click.stop href="https://www.naughtyamerica.com/terms-of-service.html" target="_blank" rel="noopener" class="text-violet-400 hover:text-violet-300 underline underline-offset-2">Terms of Service</a>
                                        and
                                        <a @click.stop href="https://www.naughtyamerica.com/privacy-policy.html" target="_blank" rel="noopener" class="text-violet-400 hover:text-violet-300 underline underline-offset-2">Privacy Policy</a>.
                                    </p>
                                </label>

                                <!-- Error -->
                                <p v-if="errorMessage" class="text-red-400 text-sm text-center mb-3">{{ errorMessage }}</p>

                                <!-- Similar casting warning -->
                                <div v-if="similarCasting" class="mb-3 rounded-xl border border-amber-500/25 bg-gradient-to-b from-amber-500/10 to-amber-600/5 px-4 py-3.5">
                                    <div class="flex items-center gap-2 mb-1.5">
                                        <div class="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                        <p class="text-amber-400 text-xs font-bold">Similar pitch already active</p>
                                    </div>
                                    <p class="text-white/50 text-xs leading-snug mb-3">
                                        "<span class="text-white/75">{{ similarCasting.title }}</span>" is already open and accepting support. Are you sure you want to submit a new one?
                                    </p>
                                    <div class="grid grid-cols-3 gap-1.5">
                                        <button @click="similarCasting = null" class="py-2 border border-white/10 bg-white/[0.03] text-white/60 font-semibold rounded-lg text-xs hover:bg-white/[0.06] transition-all">Edit</button>
                                        <button @click="goBack" class="py-2 border border-white/10 bg-transparent text-white/35 font-semibold rounded-lg text-xs hover:bg-white/[0.03] transition-all">Cancel</button>
                                        <button @click="handleSubmit(true)" :disabled="loading" class="py-2 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-lg text-xs transition-all active:scale-95 disabled:opacity-50">Submit Anyway</button>
                                    </div>
                                </div>
                            </template>

                        </div>
                    </Transition>
                </div>
                <!-- end step card -->

                </div>
                <!-- end content row -->

                <!-- Footer Navigation -->
                <div class="grid grid-cols-2 gap-3">
                    <button
                        @click="goBack"
                        class="py-3.5 border border-white/10 bg-white/[0.03] text-white/70 font-semibold rounded-xl text-sm hover:bg-white/[0.06] hover:text-white transition-all active:scale-95"
                    >
                        {{ currentStep > 1 ? '← Back' : '← Pitches' }}
                    </button>

                    <button
                        v-if="currentStep < TOTAL_STEPS"
                        @click="goNext"
                        :disabled="!canProceed"
                        :class="[
                            'py-3.5 font-bold rounded-xl text-sm transition-all duration-200',
                            canProceed
                                ? 'bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white shadow-lg shadow-violet-600/25 active:scale-[0.98]'
                                : 'bg-white/5 text-white/20 cursor-not-allowed border border-white/5'
                        ]"
                    >
                        Continue →
                    </button>

                    <button
                        v-else-if="!similarCasting"
                        @click="handleSubmit(false)"
                        :disabled="!canSubmit || loading"
                        :class="[
                            'h-[52px] font-bold rounded-xl text-sm transition-all flex items-center justify-center gap-1.5',
                            canSubmit && !loading
                                ? 'bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white shadow-lg shadow-violet-600/25 active:scale-[0.98]'
                                : 'bg-white/5 text-white/20 cursor-not-allowed border border-white/5'
                        ]"
                    >
                        <template v-if="loading">
                            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Submitting…
                        </template>
                        <template v-else>
                            <img :src="STAR_ICON" alt="" class="w-4 h-4" />
                            <span>{{ castingCost }}</span>
                            <span class="opacity-30">·</span>
                            Submit Pitch
                        </template>
                    </button>
                </div>

                <p v-if="currentStep === TOTAL_STEPS && !isUserLoggedIn" class="text-white/30 text-xs text-center mt-3 leading-snug">
                    <template v-if="castPerformer">You need a free account to submit your pitch for <a :href="castPerformerUrl" target="_blank" class="text-violet-400 hover:text-violet-300 transition-colors">{{ castPerformer.name }}</a></template>
                    <template v-else>You need a free account to submit your pitch</template>
                </p>

            </template>
        </main>
    </div>

    <CastingSignupModal
        :isOpen="isSignupModalOpen"
        source="create_scene"
        :performer="castPerformer"
        @close="isSignupModalOpen = false"
    />
    <BuyStarsModal
        :isOpen="isBuyStarsModalOpen"
        :userBalance="userBalance"
        @close="isBuyStarsModalOpen = false"
        @buy="handleBuyStarPack"
    />
</template>

<script setup>
import { ref, computed, watch, watchEffect, onMounted, onUnmounted, nextTick, h } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import axios from 'axios'
import { X, Lock, Clapperboard, Check, Lightbulb, Users, Film, Rocket, Pencil, BedDouble, Briefcase, Waves, KeyRound, Dumbbell, UtensilsCrossed, Sofa, GraduationCap, Sparkles, Monitor, Glasses, Smartphone, Layers } from 'lucide-vue-next'
import HeaderV2 from '@/Components/Reels/HeaderV2.vue'
import CastingSignupModal from '@/Components/Casting/CastingSignupModal.vue'
import BuyStarsModal from '@/Components/Reels/BuyStarsModal.vue'
import { trackFathomEvent } from '@/Helpers/analytics'

const STAR_ICON  = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'
const TOTAL_STEPS = 4

const props = defineProps({
    castingsSettings: { type: Object, required: true },
    performers:       { type: Array,  required: true },
    tags:             { type: Array,  required: true },
})

const { props: sharedProps } = usePage()

const isUserLoggedIn       = computed(() => !!(sharedProps.auth?.user?.id))
const isGoldUser           = computed(() => !!(sharedProps.auth?.is_gold))
const routePrefix          = computed(() => sharedProps.routePrefix || 'www')
const requiresAgeVerification = computed(() => !!(sharedProps.requiresAgeVerification))
const userBalance          = computed(() => Number(sharedProps.auth?.user?.wallet_balance) || 0)
const userSlug             = computed(() => sharedProps.auth?.user?.display_name || (sharedProps.auth?.user?.id ? `Naughty${sharedProps.auth.user.id}` : null))
const castingCost          = computed(() => props.castingsSettings?.default_star_cost ?? 5)

// ── Draft cookie ──────────────────────────────────────────────────────────────
const DRAFT_COOKIE = 'casting_draft'

const saveDraftCookie = () => {
    const data = {
        form: form.value,
        showCustomSetting: showCustomSetting.value,
        settingCustom: settingCustom.value,
    }
    const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString()
    const hostname = window.location.hostname
    let domainPart = ''
    if (hostname.includes('naughtyamerica.com')) {
        domainPart = '; domain=.naughtyamerica.com'
    }
    document.cookie = `${DRAFT_COOKIE}=${encodeURIComponent(JSON.stringify(data))}; expires=${expires}; path=/${domainPart}; SameSite=Lax`
}

const readDraftCookie = () => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + DRAFT_COOKIE + '=([^;]*)'))
    if (!match) return null
    try { return JSON.parse(decodeURIComponent(match[1])) } catch { return null }
}

const clearDraftCookie = () => {
    const hostname = window.location.hostname
    let domainPart = ''
    if (hostname.includes('naughtyamerica.com')) {
        domainPart = '; domain=.naughtyamerica.com'
    }
    document.cookie = `${DRAFT_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainPart}`
}

// ── Static data ───────────────────────────────────────────────────────────────
const steps = [
    { key: 'pitch',  label: 'The Pitch',  description: 'Name your idea and sell the concept.' },
    { key: 'cast',   label: 'The Cast',   description: 'Choose your performers or describe the ideal cast.' },
    { key: 'scene',  label: 'Scene',      description: 'Set the mood, tags, and director notes.' },
    { key: 'finish', label: 'Finish',     description: 'Review and submit your scene.' },
]

const successSteps = [
    'Your request enters a review queue where our team will assess feasibility and next steps.',
    'The more community support your casting gets, the stronger the signal to make it happen.',
    'Stay tuned — we\'ll keep you updated as your casting progresses.',
    'All submissions have 30 days from approval to meet their support goal. Submissions that do not meet the goal will be closed.',
]

const VrHeadsetIcon = {
    render: () => h('svg', { viewBox: '0 0 15 15', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' }, [
        h('path', { d: 'M0.851071 5.39969L0.988431 5.88046H0.988431L0.851071 5.39969ZM14.1489 5.39969L14.2863 4.91893L14.1489 5.39969ZM4.58046 12.3525L5.0194 12.5919L5.0194 12.5919L4.58046 12.3525ZM10.4195 12.3525L10.8585 12.1131L10.8585 12.1131L10.4195 12.3525ZM2.99711 3.75722L2.53287 3.57152L2.99711 3.75722ZM2.03576 4.8143C1.93321 5.0707 2.05791 5.36168 2.3143 5.46424C2.5707 5.5668 2.86168 5.44209 2.96424 5.1857L2.03576 4.8143ZM12.0029 3.75722L12.4671 3.57152V3.57152L12.0029 3.75722ZM12.0358 5.1857C12.1383 5.44209 12.4293 5.5668 12.6857 5.46424C12.9421 5.36168 13.0668 5.0707 12.9642 4.8143L12.0358 5.1857ZM14 5.86512V9.21017H15V5.86512H14ZM11.2102 12H10.668V13H11.2102V12ZM4.33198 12H3.78983V13H4.33198V12ZM1 9.21017V5.86512H0V9.21017H1ZM0.988431 5.88046C5.24432 4.66449 9.75567 4.66449 14.0116 5.88046L14.2863 4.91893C9.85084 3.65166 5.14916 3.65166 0.71371 4.91893L0.988431 5.88046ZM3.78983 12C2.24905 12 1 10.7509 1 9.21017H0C0 11.3032 1.69677 13 3.78983 13V12ZM4.14151 12.1131C4.17953 12.0434 4.25259 12 4.33198 12V13C4.61853 13 4.88219 12.8435 5.0194 12.5919L4.14151 12.1131ZM5.0194 12.5919C6.09036 10.6285 8.90964 10.6285 9.9806 12.5919L10.8585 12.1131C9.40852 9.45479 5.59148 9.45479 4.14151 12.1131L5.0194 12.5919ZM10.668 12C10.7474 12 10.8205 12.0434 10.8585 12.1131L9.9806 12.5919C10.1178 12.8435 10.3815 13 10.668 13V12ZM14 9.21017C14 10.7509 12.7509 12 11.2102 12V13C13.3032 13 15 11.3032 15 9.21017H14ZM15 5.86512C15 5.42576 14.7087 5.03963 14.2863 4.91893L14.0116 5.88046C14.0047 5.8785 14 5.87224 14 5.86512H15ZM1 5.86512C1 5.87224 0.995279 5.8785 0.988431 5.88046L0.71371 4.91893C0.291256 5.03963 0 5.42576 0 5.86512H1ZM2.53287 3.57152L2.03576 4.8143L2.96424 5.1857L3.46135 3.94291L2.53287 3.57152ZM11.5386 3.94291L12.0358 5.1857L12.9642 4.8143L12.4671 3.57152L11.5386 3.94291ZM4.85407 3H10.1459V2H4.85407V3ZM12.4671 3.57152C12.0875 2.62238 11.1682 2 10.1459 2V3C10.7593 3 11.3109 3.37343 11.5386 3.94291L12.4671 3.57152ZM3.46135 3.94291C3.68915 3.37343 4.24071 3 4.85407 3V2C3.8318 2 2.91253 2.62238 2.53287 3.57152L3.46135 3.94291Z', fill: 'currentColor' }),
    ]),
}

const formatOptions = [
    { label: 'Standard',      subtitle: '16 : 9',      icon: Monitor,       width: 'w-[78%]', aspect: 'aspect-[16/9]', ratio: '16/9' },
    { label: 'VR',            subtitle: '180° / 360°', icon: VrHeadsetIcon, width: 'w-[90%]', aspect: 'aspect-[2/1]',  ratio: '2/1' },
    { label: 'Vertical',      subtitle: '9 : 16',      icon: Smartphone,    width: 'w-[34%]', aspect: 'aspect-[9/16]', ratio: '9/16' },
    { label: 'Mixed Reality', subtitle: 'Immersive',   icon: Layers,        width: 'w-[65%]', aspect: 'aspect-[4/3]',  ratio: '4/3', layered: true },
]

const selectedFormatOption = computed(() => formatOptions.find(o => o.label === form.value.format) ?? null)

const settingOptions = [
    { label: 'Bedroom',           icon: BedDouble,        color: 'text-rose-400' },
    { label: 'Office',            icon: Briefcase,        color: 'text-blue-400' },
    { label: 'Pool / Outdoors',   icon: Waves,            color: 'text-cyan-400' },
    { label: 'Hotel Room',        icon: KeyRound,         color: 'text-amber-400' },
    { label: 'Gym / Locker Room', icon: Dumbbell,         color: 'text-orange-400' },
    { label: 'Kitchen',           icon: UtensilsCrossed,  color: 'text-yellow-400' },
    { label: 'Living Room',       icon: Sofa,             color: 'text-violet-400' },
    { label: 'Classroom',         icon: GraduationCap,    color: 'text-emerald-400' },
    { label: 'Massage Studio',    icon: Sparkles,         color: 'text-pink-400' },
]

// ── State ─────────────────────────────────────────────────────────────────────
const currentStep       = ref(1)
const slideDirection    = ref('slide-left')
const showCustomSetting = ref(false)
const settingCustom     = ref('')
const agreedToTerms     = ref(false)
const stepErrors        = ref({})
const fieldErrors       = ref({})
const errorMessage      = ref('')
const loading           = ref(false)
const submitted         = ref(false)
const castPerformer     = ref(null)
const castPerformerUrl  = computed(() => castPerformer.value?.slug ? route(routePrefix.value + '.performer.show', { slug: castPerformer.value.slug }) : null)
const submittedSlug     = ref(null)
const similarCasting    = ref(null)

const form = ref({
    title: '',
    performers: [],
    performerDescription: '',
    format: null,
    tags: [],
    description: '',
    setting: [],
    directorNotes: '',
})

// ── Performers ────────────────────────────────────────────────────────────────
const performerInput            = ref('')
const allPerformers             = ref(props.performers)
const isLoadingPerformers       = ref(false)
const highlightedPerformerIndex = ref(-1)
const performerInputContainer   = ref(null)
const performerDropdown         = ref(null)
const performerInputFocused     = ref(false)

const performerDropdownStyle = ref(null)
watchEffect(async () => {
    // Track all reactive deps that affect input height before the await
    const _input = performerInput.value
    const _pLen  = form.value.performers.length
    const _desc  = form.value.performerDescription
    await nextTick()
    if (!performerInputContainer.value || _input.trim().length < 1) {
        performerDropdownStyle.value = null
        return
    }
    const rect = performerInputContainer.value.getBoundingClientRect()
    performerDropdownStyle.value = {
        top:       `${rect.bottom + 6}px`,
        left:      `${rect.left}px`,
        width:     `${rect.width}px`,
        maxHeight: '200px',
        overflowY: 'auto',
    }
})


const MAX_VISIBLE_PERFORMER_CHIPS = 3

const visiblePerformerChips   = computed(() => form.value.performers.slice(0, MAX_VISIBLE_PERFORMER_CHIPS))
const hiddenPerformerChipCount = computed(() => Math.max(0, form.value.performers.length - MAX_VISIBLE_PERFORMER_CHIPS))

const suggestions = computed(() => {
    const q = performerInput.value.trim().toLowerCase()
    if (!q) return []
    const selectedIds = new Set(form.value.performers.map(p => p.id))
    return allPerformers.value
        .filter(p => !selectedIds.has(p.id) && p.name.toLowerCase().includes(q))
        .slice(0, 10)
})

const selectPerformer = (performer) => {
    if (form.value.performers.length >= 4) return
    form.value.performers.push(performer)
    form.value.performerDescription = ''
    performerInput.value = ''
    highlightedPerformerIndex.value = -1
}

const removePerformer = (id) => {
    if (isLockedPerformer(id)) return
    form.value.performers = form.value.performers.filter(p => p.id !== id)
    if (fieldErrors.value.performers) delete fieldErrors.value.performers
}

// ── Performer carousel ─────────────────────────────────────────────────────────
const CARD_GAP          = 10
const CAROUSEL_VISIBLE  = 5
const CAROUSEL_SPEED    = 55  // px per second
const MANUAL_PAUSE_MS   = 1800 // ms to pause after last manual scroll input

const carouselHovered       = ref(false)
const carouselViewport      = ref(null)
const carouselContainerWidth = ref(0)
let   carouselRaf           = null
let   manualScrollTimer     = null

const cardWidth = computed(() =>
    carouselContainerWidth.value
        ? Math.floor((carouselContainerWidth.value - (CAROUSEL_VISIBLE - 1) * CARD_GAP) / CAROUSEL_VISIBLE)
        : 80
)

const shouldAnimate = computed(() => form.value.performers.length > CAROUSEL_VISIBLE)

const carouselItems = computed(() =>
    shouldAnimate.value
        ? [...form.value.performers, ...form.value.performers]
        : form.value.performers
)

const singleSetWidth = computed(() =>
    form.value.performers.length * (cardWidth.value + CARD_GAP)
)

// RAF-based auto-scroll — runs always, skips increment when paused
let lastTime = null
const carouselTick = (ts) => {
    carouselRaf = requestAnimationFrame(carouselTick)
    const el = carouselViewport.value
    if (!el || !shouldAnimate.value || carouselHovered.value) {
        lastTime = null
        return
    }
    if (lastTime === null) { lastTime = ts; return }
    const delta = ((ts - lastTime) / 1000) * CAROUSEL_SPEED
    lastTime = ts
    el.scrollLeft += delta
    // seamless loop
    if (el.scrollLeft >= singleSetWidth.value) {
        el.scrollLeft -= singleSetWidth.value
    }
}

const startCarouselRaf = () => {
    if (carouselRaf) return
    carouselRaf = requestAnimationFrame(carouselTick)
}

// manual scroll input — pause auto-scroll temporarily
const onManualScroll = () => {
    carouselHovered.value = true
    clearTimeout(manualScrollTimer)
    manualScrollTimer = setTimeout(() => {
        if (!carouselHovered.value) return // still hovered, don't resume
        carouselHovered.value = false
        lastTime = null
    }, MANUAL_PAUSE_MS)
}

const onWheel = (e) => {
    if (!shouldAnimate.value) return
    e.preventDefault()
    carouselViewport.value.scrollLeft += e.deltaY + e.deltaX
    onManualScroll()
}

const pauseCarousel  = () => { carouselHovered.value = true;  clearTimeout(manualScrollTimer); lastTime = null }
const resumeCarousel = () => { carouselHovered.value = false; lastTime = null }

watch(() => form.value.performers.length, async (len) => {
    if (len > 0 && !carouselContainerWidth.value) {
        await nextTick()
        if (carouselViewport.value) {
            carouselContainerWidth.value = carouselViewport.value.offsetWidth
        }
    }
})

watch(carouselViewport, (el) => {
    if (el) {
        carouselContainerWidth.value = el.offsetWidth
    }
})

onUnmounted(() => {
    if (carouselRaf) cancelAnimationFrame(carouselRaf)
    clearTimeout(manualScrollTimer)
})

const canAddAsDescription = computed(() => performerInput.value.trim().length >= 1)

const addAsDescription = () => {
    const text = performerInput.value.trim()
    if (!text) return
    form.value.performerDescription = text
    performerInput.value = ''
    highlightedPerformerIndex.value = -1
}

const isLockedPerformer = (id) => castPerformer.value?.id === id

const clearAllPerformers = () => {
    form.value.performers = castPerformer.value ? [castPerformer.value] : []
    form.value.performerDescription = ''
    performerInput.value = ''
    highlightedPerformerIndex.value = -1
}

const onPerformerKeydown = (e) => {
    if (e.key === 'Escape') { performerInput.value = ''; highlightedPerformerIndex.value = -1; return }
    const hasAdd = canAddAsDescription.value
    const total  = suggestions.value.length + (hasAdd ? 1 : 0)
    if (!total) return
    if (e.key === 'ArrowDown') {
        e.preventDefault()
        highlightedPerformerIndex.value = Math.min(highlightedPerformerIndex.value + 1, total - 1)
    } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        highlightedPerformerIndex.value = Math.max(highlightedPerformerIndex.value - 1, -1)
    } else if (e.key === 'Enter') {
        const i = highlightedPerformerIndex.value
        if (i >= 0 && i < suggestions.value.length) {
            e.preventDefault()
            selectPerformer(suggestions.value[i])
        } else if (hasAdd && (i === suggestions.value.length || !suggestions.value.length)) {
            e.preventDefault()
            addAsDescription()
        }
    }
}

watch(suggestions, () => { highlightedPerformerIndex.value = -1 })

watch(highlightedPerformerIndex, async (i) => {
    if (i < 0 || !performerDropdown.value) return
    await nextTick()
    const buttons = performerDropdown.value.querySelectorAll('button')
    buttons[i]?.scrollIntoView({ block: 'nearest' })
})

// ── Tags ──────────────────────────────────────────────────────────────────────
const tagInput          = ref('')
const allTags           = ref(props.tags)
const isLoadingTags     = ref(false)
const tagInputContainer = ref(null)

const tagDropdownStyle = computed(() => {
    if (!tagInputContainer.value) return null
    const rect = tagInputContainer.value.getBoundingClientRect()
    return {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: '200px',
        overflowY: 'auto',
    }
})


const tagSuggestions = computed(() => {
    const q = tagInput.value.trim().toLowerCase()
    if (!q) return []
    const selectedIds = new Set(form.value.tags.map(t => t.id))
    return allTags.value
        .filter(t => !selectedIds.has(t.id) && t.name.toLowerCase().includes(q))
        .slice(0, 10)
})

const selectTag = (tag) => { form.value.tags.push(tag); tagInput.value = '' }
const removeTag = (id)  => { form.value.tags = form.value.tags.filter(t => t.id !== id) }

// ── Settings toggle ───────────────────────────────────────────────────────────
const toggleSetting = (option) => {
    const idx = form.value.setting.indexOf(option)
    if (idx === -1) form.value.setting.push(option)
    else form.value.setting.splice(idx, 1)
}

// ── Navigation ────────────────────────────────────────────────────────────────
const canProceed = computed(() => {
    if (currentStep.value === 1) return form.value.title.trim().length > 0 && form.value.description.trim().length > 0
    if (currentStep.value === 2) return form.value.performers.length > 0 || form.value.performerDescription.trim().length > 0
    if (currentStep.value === 3) return form.value.format !== null
    return true
})

const goNext = () => {
    stepErrors.value = {}
    if (currentStep.value === 1 && !canProceed.value) {
        if (!form.value.title.trim())       stepErrors.value.title       = 'Please add a title for your pitch.'
        if (!form.value.description.trim()) stepErrors.value.description = 'Please describe your concept.'
        return
    }
    if (currentStep.value === 2 && !canProceed.value) { stepErrors.value.cast   = 'Please select a performer or name specific performers.'; return }
    if (currentStep.value === 3 && !canProceed.value) { stepErrors.value.format = 'Please select a format.'; return }
    slideDirection.value = 'slide-left'
    currentStep.value++
    trackFathomEvent(`Casting Wizard Step ${currentStep.value} Reached`)
}

const goToStep = (step) => {
    slideDirection.value = 'slide-right'
    currentStep.value = step
}

const goBack = () => {
    if (currentStep.value > 1) {
        slideDirection.value = 'slide-right'
        currentStep.value--
    } else {
        router.visit('/feed/auditions?view=castings')
    }
}

const goToCastingsPage = () => {
    if (userSlug.value) {
        router.visit(`/u/${userSlug.value}/castings`)
    } else {
        router.visit('/feed/auditions?view=castings')
    }
}

// ── Submission ────────────────────────────────────────────────────────────────
const canSubmit = computed(() => agreedToTerms.value)
const isSignupModalOpen = ref(false)
const isBuyStarsModalOpen = ref(false)

const handleBuyStarPack = (pack) => {
    // Redirect to purchase URL if available
    if (pack.purchaseUrl) {
        window.location.href = pack.purchaseUrl
    }
    // Modal will close automatically or user can close it
}

const handleSubmit = async (force = false) => {
    if (!canSubmit.value) return

    saveDraftCookie()

    if (!isUserLoggedIn.value) {
        isSignupModalOpen.value = true
        return
    }
    trackFathomEvent('Casting Wizard Pitch Submitted')

    loading.value      = true
    errorMessage.value = ''
    fieldErrors.value  = {}
    similarCasting.value = null

    const setting = [
        ...form.value.setting,
        ...(showCustomSetting.value && settingCustom.value.trim() ? [settingCustom.value.trim()] : []),
    ]

    try {
        const { data } = await axios.post('/feed/api/casting', {
            title:                 form.value.title || null,
            performers:            form.value.performers.map(p => p.id),
            performer_description: form.value.performerDescription || null,
            format:                form.value.format || null,
            tags:                  form.value.tags,
            description:           form.value.description || null,
            setting,
            director_notes:        form.value.directorNotes || null,
            is_for_performer:      castPerformer.value ? true : false,
            performer_id:          castPerformer.value?.id || null,
            force,
        })

        clearDraftCookie()
        submittedSlug.value = data.casting_slug ?? null
        submitted.value = true
    } catch (e) {
        if (e.response?.status === 409 && e.response?.data?.similar) {
            similarCasting.value = e.response.data.existing
        } else if (e.response?.status === 422 && e.response?.data?.errors) {
            const errors = e.response.data.errors
            fieldErrors.value = Object.keys(errors).reduce((acc, key) => {
                acc[key] = Array.isArray(errors[key]) ? errors[key][0] : errors[key]
                return acc
            }, {})
            // Navigate to the step with the first error
            const step1Fields = ['title', 'description', 'director_notes']
            const step2Fields = ['performers', 'performer_description']
            const step3Fields = ['format', 'setting', 'tags']
            if (step1Fields.some(f => fieldErrors.value[f])) { slideDirection.value = 'slide-right'; currentStep.value = 1 }
            else if (step2Fields.some(f => fieldErrors.value[f])) { slideDirection.value = 'slide-right'; currentStep.value = 2 }
            else if (step3Fields.some(f => fieldErrors.value[f])) { slideDirection.value = 'slide-right'; currentStep.value = 3 }
        } else {
            errorMessage.value = e.response?.data?.message || 'Something went wrong. Please try again.'
        }
    } finally {
        loading.value = false
    }
}

// ── Init ──────────────────────────────────────────────────────────────────────
onMounted(() => {
    startCarouselRaf()

    const draft = readDraftCookie()
    if (draft) {
        form.value              = { ...form.value, ...draft.form }
        showCustomSetting.value = draft.showCustomSetting ?? false
        settingCustom.value     = draft.settingCustom ?? ''
    }

    const castParam = new URLSearchParams(window.location.search).get('cast')
    if (castParam) {
        const name = castParam.trim().toLowerCase()
        const match = allPerformers.value.find(p => p.name.toLowerCase() === name)
        if (match) {
            if (!form.value.performers.some(p => p.id === match.id) && form.value.performers.length < 4) {
                form.value.performers.push(match)
            }
            castPerformer.value = match
        }
    }
})
</script>

<style scoped>
.page-bg {
    background: linear-gradient(160deg, #0f0f17 0%, #0a0a10 100%);
}

.page-card {
    background: linear-gradient(160deg, #0f0f17 0%, #0a0a10 100%);
    border: 1px solid rgba(255, 255, 255, 0.06);
}

/* Override global blue focus rule */
.page-card textarea:focus,
.page-card select:focus,
.page-card input:focus,
.page-card [type='text']:focus {
    border-color: rgba(139, 92, 246, 0.5) !important;
    --tw-ring-shadow: rgba(139, 92, 246, 0.08) !important;
}

.field-label {
    @apply text-white/70 text-xs font-semibold block mb-1.5 tracking-wide;
}
.field-input {
    @apply w-full px-3.5 py-2.5 bg-zinc-900/70 border border-white/10 rounded-xl text-white text-sm
           placeholder:text-white/20 focus:outline-none focus:border-violet-500/50
           focus:shadow-[0_0_0_3px_rgba(139,92,246,0.08)] transition-all;
}
.field-error {
    @apply border-red-500/50 focus:border-red-500/70 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.08)];
}
.field-error-msg {
    @apply text-red-400 text-xs mt-1;
}

.casting-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.08) transparent;
}
.casting-scroll::-webkit-scrollbar       { width: 3px; }
.casting-scroll::-webkit-scrollbar-track { background: transparent; }
.casting-scroll::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.08);
    border-radius: 9999px;
}
.casting-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(255,255,255,0.15);
}

/* Performer carousel viewport */
.carousel-viewport {
    scrollbar-width: none;
    -ms-overflow-style: none;
}
.carousel-viewport::-webkit-scrollbar { display: none; }

/* Step label fade */
.fade-step-enter-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.fade-step-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.fade-step-enter-from   { opacity: 0; transform: translateY(4px); }
.fade-step-leave-to     { opacity: 0; transform: translateY(-4px); }

/* Carousel container reveal */
.carousel-reveal-enter-active { transition: opacity 0.3s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); }
.carousel-reveal-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.carousel-reveal-enter-from  { opacity: 0; transform: translateY(-8px) scaleY(0.95); transform-origin: top; }
.carousel-reveal-leave-to    { opacity: 0; transform: translateY(-4px) scaleY(0.97); transform-origin: top; }

/* Step slide transitions */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease;
}
.slide-left-enter-from  { transform: translateX(32px);  opacity: 0; }
.slide-left-leave-to    { transform: translateX(-32px); opacity: 0; }
.slide-right-enter-from { transform: translateX(-32px); opacity: 0; }
.slide-right-leave-to   { transform: translateX(32px);  opacity: 0; }

.text-white\/35 { color: #ffffff90; }
.text-white\/25 { color: #ffffff90; }
.field-input::placeholder { color: #fff6; }
</style>
